import { Injectable } from '@angular/core';
import { FitApiClientService, FitList } from '@shared/fit-api-client.service';
import { ProgramItemCustomerResponse } from '@app/program/types';
import { GalleryItem } from '@app/gallery/gallery.service';
import { QuestionTypes } from '@app/questionnaire/types';
import { UserActivityItem } from '@app/user-activity/types';
import { ClientChat } from '@app/resolution-center/resolution-center.service';

export { ProgramItemCustomerResponse };

interface NutritionProgram {
    id: number;
    name: string;
}

export interface User {
    id: number;
    first_name: string;
    last_name: string;
    is_active: boolean;
    flow?: string;
}

export interface Administrator extends User {
    id: number;
    email: string;
    gender: 'male' | 'female' | 'other';
    image: GalleryItem;
    password: string;
    passwordConfirm: string;
}

export interface AdministratorList extends User {
    image_url: string;
}

export interface Coach extends Administrator {}

export interface CoachList extends Administrator {
    name: string;
}

interface AdministratorRequest extends User {
    email: string;
    gender: 'male' | 'female' | 'other';
    image_id: number;
    password: string;
}

interface CoachRequest extends AdministratorRequest {}

export interface Customer extends User {
    email: string;
    gender: 'male' | 'female';
    is_survey_completed: boolean;
    fullname?: string;
    avatar: string;
    signup_date: string;
    last_login: string;
    active_package: Package;
    activated_nutrition_programs: { id: number, name: string }[];
    activated_programs: {id: number, name: string}[];
}

export interface NewCustomer {
    first_name: string;
    last_name: string;
    email: string;
    password?: string;
}

export interface CustomerById extends User {
    email: string;
    gender: 'male' | 'female';
    bio: string;
    image: GalleryItem;
    cover_image: GalleryItem;
}

interface Package {
    id: number;
    name: string;
    recurring_period_in_days?: number;
}

/*
export type queryHealth = 'height' | 'weight' | 'steps' | 'heart_rate' | 'respiratory_rate' | 'energy_consumed' | 'body_fat' | 'sleep' | 
                          'calories_intake' | 'proteins_intake' | 'carbohydrates_intake' | 'fat_intake';
*/

export interface UserDetails {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    image_url: string;
    cover_image_url: string;
    account_created_at: string;
    recent_fitness_timestamp?: string;
    referral_code?: string;
    referral_link?: string;
    user_agent?: string;
    platform?: string;
    daily_streak: number;
    trainings_completed_count: number;
    programs_completed_count: number;
    nutrition_programs_completed_count: number;
    is_active: boolean;
    is_deleted: boolean;
    timezone?: string;
    pay_to_play_enabled: boolean;
    bypass_app_paywall_until?: string;
    last_completed_training: any;
    last_logged_meal: any;
    logged_meals_count: number;
    training_activity: UserActivityItem[];
    streaks: {
        current_daily_streak: number;
        longest_daily_streak: number;
        current_weakly_streak: number;
        longest_weekly_streak: number;
    };
    activated_programs: ActivatedProgram[];
    activated_nutrition_programs: any[];
    achievements: Achievement[];
    tags: UserTag[];
    notes: UserNote[];
    progress_images: ProgressImage[];
    height_in_centimeters: number;
    weight_in_kilograms: number;
    age: number;
    is_blocked_from_posting: boolean;
    macronutrients: MacroNutritients;
    subscription_info?: CustomerSubscriptionInfo;
    billing_info?: CustomerBillingInfo;
    thread?: ClientChat;
    teams: Team[];
    coaches: NamedEntity[];
    questionnaire_responses: QuestionnaireResponse[];
    payment_transactions: CustomerTransaction[];
    total_spend: UserTotalSpend;
    course_stats?: CourseStats;
}

interface CustomerTransaction{
    charged_value: number;
    currency_code: string;
    is_test: boolean;
    email: string;
    method: string;
    paymentItem: string;
    status: string;
    timestamp: string;
    transaction_id: string;
    type: string;
}

interface UserTotalSpend {
    amount: number;
    currency_code: string;
}

export interface CustomerSubscriptionInfo {
    package_name?: string;
    recurring_period_in_days?: number;
    valid_until: string;
    is_active_subscriber: boolean;
    last_renewal_attempt?: string;
    renew_stopped: boolean;
    payment_method?: string;
    is_free_trial: boolean;
    charged_value?: number;
    currency_code?: string;
}

interface CustomerBillingInfo{
    name: string;
    surname: string;
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
    stripe_id?: string;
    cards: any[];
}

export interface QuestionnaireResponse {
    id: number;
    questionnaire_id: number;
    questionnaire_name: string;
    questionnaire_type: string;
    completed_at: string;
    response: QuestionnaireResponseResponse[];
    notes?: UserNote[];
}

interface QuestionnaireResponseResponse {
    type: QuestionTypes;
    text: string;
    answers: QuestionnaireAnswer[];
}

interface QuestionnaireAnswer {
    text: string;
}

export interface MacroNutritients {
    carbs: MacroNutritientItem;
    daily_goals: MacroNutritientItem;
    fats: MacroNutritientItem;
    fiber: MacroNutritientItem;
    protein: MacroNutritientItem;
    sugar: MacroNutritientItem;
    water: MacroNutritientItem;
}

export type MacroNutritientType = keyof MacroNutritients;

interface MacroNutritientRequestItem {
    value: number;
    unit: string | null;
}

type MacroNutritientsRequest = Record<MacroNutritientType, MacroNutritientRequestItem>;

interface MacroNutritientItem {
    type?: MacroNutritientType;
    title: string;
    requirements: number;
    intake?: number;
    deficit?: number;
    percentage?: number;
    unit: string | null;
}

export interface MacronutrientsTrackingMetric {
    type: string;
    goal: number;
    goal_unit: string;
    intake: number;
    deficit: number;
}

export interface MacronutrientsTrackingDay {
    date: string;
    macronutrients: MacronutrientsTrackingMetric[];
}

interface CustomerActivityData {
    id: number;
    name: string;
    completed_at: string;
}

export interface WeekActivity {
    date: string;
    programs: ProgramActivity[];
    trainings: TrainingActivity[];
    meals: MealActivity[];
}

interface ProgramActivity {
    id: number;
    name: string;
    week: number;
    day: number;
    exercises: ExerciseLog[];
    comment?: string;
}

interface TrainingActivity {
    id: number;
    name: string;
    exercises: ExerciseLog[];
    comment?: string;
}

interface MealActivity {
    id: number;
    name: string;
    number_of_calories: number;
    servings: number;
    logged_times: number;
}
interface ExerciseLog {
    name: string;
    number_of_completed_exercises: number;
    user_time_in_seconds?: number;
    number_of_reps?: number;
    is_reps_descriptive: boolean;
    weight_in_kilograms_float?: number;
    is_weight_descriptive: boolean;
    strength_percentage?: number;
}

export interface StepsData {
    ends_at: string;
    starts_at: string;
    timestamp?: string;
    unit: string;
    value: number | string;
    type?: string;
    avg?: number;
    min?: any;
    max?: any;
}

export interface Team {
    id: number;
    name: string;
}

export interface NamedEntity {
    id: number;
    name: string;
}

export interface CourseStats {
    number_of_finished_lessons: number;
    number_of_favorite_lessons: number;
    completion_percentage: number;
}

export interface EnergyConsumedData extends StepsData { }
export interface HeartRateData extends StepsData { }
export interface HeightData extends StepsData { }
export interface WeightData extends StepsData { }

interface HealthData extends StepsData { }

export interface HealthSummary {
    average_heart_rate: number;
    max_heart_rate: number;
    min_heart_rate: number;
    average_respiratory_rate: number;
    max_respiratory_rate: number;
    min_respiratory_rate: number;
    current_height_in_centimeters: number;
    current_weight_in_kilograms: number;
    total_energy_consumed: number;
    total_steps: number;
    total_sleep: number;
    total_calories_intake: number;
    total_proteins_intake: number;
    total_carbohydrates_intake: number;
    total_fat_intake: number;
}

export interface ActivatedProgram {
    id: number;
    name: string;
    image_url: string;
    percentage_completed: number;
}

export type LoadDay = 'previous' | 'next';

export interface Achievement {
    title: string;
    description: string;
    image_url: string;
    is_unlocked: boolean;
}

export interface UserTagList {
    name: string;
    label?: string;
    description?: string;
    tags: UserTag[];
}

export interface UserTag {
    id: number;
    name: string;
    description?: string;
    is_custom_tag: boolean;
    items_count: number;
}

export interface UserNote {
    id: number;
    content: string;
    created_by: string;
    created_at: string;
}

export interface ProgressImage {
    id?: number;
    image_url: string;
    name?: string;
    weight_in_kilograms?: number;
    height_in_centimeters?: number;
    waist_in_centimeters?: number;
    display_date: string;
}

export interface CustomerActivityCursorMeta {
    next_cursor: string | null;
    has_more: boolean;
}

export interface CustomerActivityFeed {
    items: UserActivityItem[];
    meta: CustomerActivityCursorMeta;
}

export interface CustomerQuestionnaireCursorMeta {
    next_cursor: string | null;
    has_more: boolean;
}

export interface CustomerQuestionnaireFeed {
    items: any[];
    meta: CustomerQuestionnaireCursorMeta;
}

export interface CustomerQuestionnaireBreakdown {
    total_all_time: number;
    total_this_month: number;
    completion_rate: number;
    pending: number;
}

export type CustomerCalendarActivityType =
    | 'Training'
    | 'Meal'
    | 'Scheduled Event'
    | 'Live Event'
    | 'Program'
    | 'Recipe Book'
    | 'Progress Image';

export interface CustomerCalendarActivity {
    id: number;
    title: string;
    image_url: string | null;
    type: CustomerCalendarActivityType;
    object_type: string;
    object_id: number;
    metadata: Record<string, unknown> | null;
    is_missed: boolean;
}

export interface CustomerCalendarDay {
    date: string;
    activities: CustomerCalendarActivity[];
}

export interface CustomerCalendarActivityAssignItem {
    object_id: number;
    object_type: 'meal' | 'training' | 'program' | 'nutrition_program';
}

export interface CustomerCalendarActivitiesAssignRequest {
    items: CustomerCalendarActivityAssignItem[];
    date: string;
    period: 'week' | 'month';
}

export interface CustomerCalendarActivityMoveRequest {
    date: string;
    period: 'week' | 'month';
}

export interface UserMealDetails {
    meal_name: string;
    meal_image_url: string | null;
    meal_group_name: string;
    number_of_servings: number;
    number_of_calories: number;
    fat: number;
    proteins: number;
    carbohydrates: number;
    logged_at: string;
}

export interface UserNoteCursorMeta {
    next_cursor: string | null;
    has_more: boolean;
}

export interface UserNoteObject {
    id: number;
    type: string;
    name: string;
}

export interface UserNoteFeedItem extends UserNote {
    updated_at?: string;
    object: UserNoteObject;
}

export interface UserNoteFeed {
    items: UserNoteFeedItem[];
    meta: UserNoteCursorMeta;
}

export interface UserNoteBreakdown {
    total_all_time: number;
    total_this_month: number;
    high_priority: number;
    pinned: number;
}

export {
    FitList,
    NutritionProgram,
    Package,
};

@Injectable({ providedIn: 'root' })

export class UserService {
    constructor(private fitApi: FitApiClientService) { }

    public searchAdministrator(query: string, page: number) {
        return this.fitApi.get<FitList<AdministratorList>>(
            '/admin/administrator',
            {
                search: query,
                page: page.toString(),
            }
        );
    }

    public getAdministrator(id: number) {
        return this.fitApi.get<Administrator>('/admin/administrator/' + id);
    }

    public createAdministrator(admin: Administrator) {
        return this.fitApi.put<Administrator>(
            '/admin/administrator/0',
            this.mapAdministratorRequest(admin)
        );
    }

    public updateAdministrator(admin: Administrator) {
        return this.fitApi.put<Administrator>(
            '/admin/administrator/' + admin.id,
            this.mapAdministratorRequest(admin)
        );
    }

    public deleteAdministrator(id: number) {
        return this.fitApi.delete<any>(`/admin/administrator/${id}`);
    }

    public searchCoach(query: string, page: number) {
        return this.fitApi.get<FitList<CoachList>>(
            '/admin/coach',
            {
                search: query,
                page: page.toString(),
            }
        );
    }

    public getAllCoaches(appId: number, query?: string) {
        const queryParams: any = {
            application_id: appId.toString()
        };

        if (query) {
            queryParams.search = query;
        }

        return this.fitApi.get<CoachList[]>('/admin/coach/list', queryParams);
    }

    public getCoach(id: number) {
        return this.fitApi.get<Coach>('/admin/coach/' + id);
    }

    public createCoach(coach: Coach) {
        return this.fitApi.put<Coach>('/admin/coach/0', this.mapCoachRequest(coach));
    }

    public updateCoach(coach: Coach) {
        return this.fitApi.put<Coach>('/admin/coach/' + coach.id, this.mapCoachRequest(coach));
    }

    public saveMacronutritients(user_id: number, macronutrients: MacroNutritients) {
        return this.fitApi.put<MacroNutritientItem[]>(
            `/admin/customer/${user_id}/macronutrients`,
            this.mapMacronutrientsToRequest(macronutrients)
        ).then((response) => {
            if (!response.error && Array.isArray(response.data)) {
                return {
                    ...response,
                    data: this.mapMacronutrientsToObject(response.data)
                };
            }
            return response;
        });
    }

    public searchUser(
        query: string,
        page: number,
        isSubscriber?: boolean,
        notSubscriber?: boolean,
        isActiveSubscriber?: boolean,
        isTailor?: boolean,
        isWithNoLogin?: boolean,
        programs?: string,
        tagIds?: number[],
        statusTagIds?: number[],
        perPage?: number,
    ) {

        const queryParams: any = {
            search: query,
            page: page.toString()
        };

        if (perPage) {
            queryParams.per_page = perPage.toString();
        }

        if (isSubscriber) {
            queryParams.isSubscriber = `${isSubscriber}`;
        }

        if (notSubscriber) {
            queryParams.isSubscriber = `false`;
        }

        if (isActiveSubscriber) {
            queryParams.isActiveSubscriber = `${isActiveSubscriber}`;
        }

        if (isTailor) {
            queryParams.isTailorMade = `${isTailor}`;
        }

        if (isWithNoLogin) {
            queryParams.withNoLogin = `${isWithNoLogin}`;
        }

        if (programs) {
            queryParams.programIds = `${programs}`;
        }

        if (tagIds?.length) {
            queryParams.tag_ids = tagIds.join(',');
        }

        if (statusTagIds?.length) {
            queryParams.status_tag_ids = statusTagIds.join(',');
        }

        return this.fitApi.get<FitList<Customer>>('/admin/customer', queryParams);
    }

    public getUser(id: number) {
        return this.fitApi.get<CustomerById>(`/admin/customer/${id}`);
    }

    public getUserDetails(id: number) {
        return this.fitApi.get<UserDetails>(`/admin/customer/${id}/profile`)
            .then((response) => {
                if (Array.isArray(response?.data?.macronutrients)) {
                    response.data.macronutrients = this.mapMacronutrientsToObject(response.data.macronutrients);
                }
                return response;
            });
    }

    public createNewCustomer(user: NewCustomer) {
        return this.fitApi.put<any>('/admin/customer', user);
    }

    public createUser(user: User) {
        return this.fitApi.put<CustomerById>('/admin/customer/0', user);
    }

    public updateUser(user: User) {
        return this.fitApi.put<CustomerById>('/admin/customer/' + user.id, user);
    }

    public unsubscribeUser(userId: number) {
        return this.fitApi.put<CustomerById>(`/admin/customer/${userId}/unsubscribe`, null);
    }

    public terminateUserAccess(userId: number) {
        return this.fitApi.put<CustomerById>(`/admin/customer/${userId}/terminate`, null);
    }

    public blockFromPosting(userId: number) {
        return this.fitApi.put<CustomerById>(`/admin/customer/${userId}/block-from-posting`, null);
    }

    public unblockFromPosting(userId: number) {
        return this.fitApi.put<CustomerById>(`/admin/customer/${userId}/unblock-from-posting`, null);
    }

    public assignPackageToUser(userId: number, packageId: number) {
        return this.fitApi.post<CustomerById>('/admin/packages/assign', {
            user_id: userId,
            package_id: packageId,
        });
    }

    public deleteUser(id: number) {
        return this.fitApi.delete<any>('/admin/customer/' + id);
    }

    public insertFakeCard(userId: number) {
        return this.fitApi.put<CustomerById>(`/admin/customer/${userId}/add-fake-payment-card`, null);
    }

    public updateCustomerEmail(id: number, email: string) {
        return this.fitApi.put<CustomerById>(`/admin/customer/${id}/update`, { email: email });
    }

    public updateCustomerUserTags(id: number, tags: UserTag[]) {
        const tagIds = tags.map(tag => tag.id);
        return this.fitApi.put<CustomerById>(`/admin/customer/${id}/update`, { user_tag_ids: tagIds });
    }

    public getUserTagLists() {
        return this.fitApi.get<UserTagList[]>(
            '/admin/tag/user'
        );
    }

    public getCustomUserTags() {
        return this.fitApi.get<UserTag[]>(
            '/admin/tag/custom'
        );
    }

    public createCustomUserTag(tag: UserTag) {
        return this.fitApi.put<UserTag>('/admin/tag/0', tag);
    }

    public saveNote(objectId: number, objectType: string, note: string, noteId?: number) {
        return this.fitApi.put<UserNote | UserNote[]>(
            `/admin/note/${noteId ? noteId.toString() : '0'}`,
            {
                object_id: objectId,
                object_type: objectType,
                note_content: note
            }
        );
    }

    public deleteNote(noteId: number) {
        return this.fitApi.delete<any>(`/admin/note/${noteId}`);
    }

    public getUserNotes(customerId: number, cursor?: string | null, search?: string, limit?: number) {
        const queryParams: { [key: string]: string } = {
            user_id: customerId.toString(),
        };

        if (cursor) {
            queryParams.cursor = cursor;
        }
        if (search?.trim()) {
            queryParams.search = search.trim();
        }
        if (limit && limit > 0) {
            queryParams.limit = limit.toString();
        }

        return this.fitApi.get<{ data: UserNoteFeed }>('/admin/note', queryParams);
    }

    public getUserNotesBreakdown(customerId: number) {
        return this.fitApi.get<{ data: UserNoteBreakdown }>('/admin/note/breakdown', {
            user_id: customerId.toString(),
        });
    }

    public getUserPrograms(userId: number) {
        return this.fitApi.get<ProgramItemCustomerResponse[]>(
            `/admin/user/${userId}/purchased-programs`
        );
    }

    public getPackages() {
        return this.fitApi.get<FitList<Package>>(`/admin/packages`);
    }

    public getUserNutritionPrograms(userId: number) {
        return this.fitApi.get<NutritionProgram[]>(
            `/admin/customer/${userId}/nutrition-programs`
        );
    }

    public addRole(user: User, role: string) {
        const params = {
            roles: [role]
        }
        return this.fitApi.put<CustomerById>(`/admin/user/${user.id}/assign-roles`, params);
    }

    public downloadUserList(query: string,
        page: number,
        isSubscriber?: boolean,
        notSubscriber?: boolean,
        isActiveSubscriber?: boolean,
        isTailor?: boolean,
        isWithNoLogin?: boolean,
        programs?: string) {

        const queryParams: any = {
            search: query,
            page: page.toString()
        };

        if (isSubscriber) {
            queryParams.isSubscriber = `${isSubscriber}`;
        }

        if (notSubscriber) {
            queryParams.isSubscriber = `false`;
        }

        if (isActiveSubscriber) {
            queryParams.isActiveSubscriber = `${isActiveSubscriber}`;
        }

        if (isTailor) {
            queryParams.isTailorMade = `${isTailor}`;
        }

        if (isWithNoLogin) {
            queryParams.withNoLogin = `${isWithNoLogin}`;
        }

        if (programs) {
            queryParams.programIds = `${programs}`;
        }

        //return this.fitApi.get<string>(`/admin/reports/users-export-csv-url`, queryParams);
        return this.fitApi.get<any>(`/admin/reports/users-export-xls`, queryParams);
    }

    public removeRole(userId: number, role: string) {
        const params = {
            roles: [role]
        }
        return this.fitApi.put<CustomerById>(`/admin/user/${userId}/remove-roles`, params);
    }

    public getCompletedPrograms(customerId: number, page?: number) {
        const queryParams: any = {
            page: page ? page.toString() : '1'
        };

        return this.fitApi.get<FitList<CustomerActivityData>>(`/admin/customer/${customerId}/completed-programs`, queryParams);
    }

    public getLoggedTrainings(customerId: number, page?: number) {
        const queryParams: any = {
            page: page ? page.toString() : '1'
        };

        return this.fitApi.get<FitList<CustomerActivityData>>(`/admin/customer/${customerId}/completed-trainings`, queryParams);
    }

    public getCompletedNutritionPrograms(customerId: number, page?: number) {
        const queryParams: any = {
            page: page ? page.toString() : '1'
        };

        return this.fitApi.get<FitList<CustomerActivityData>>(`/admin/customer/${customerId}/completed-nutrition-programs`, queryParams);
    }

    public getLoggedRecipes(customerId: number, page?: number) {
        const queryParams: any = {
            page: page ? page.toString() : '1'
        };

        return this.fitApi.get<FitList<CustomerActivityData>>(`/admin/customer/${customerId}/logged-recipes`, queryParams);
    }

    public getProgressImages(customerId: number, page?: number) {
        const queryParams: any = {
            page: page ? page.toString() : '1'
        };

        return this.fitApi.get<FitList<ProgressImage>>(`/admin/customer/${customerId}/progress-image`, queryParams);
    }

    public getCustomerActivities(customerId: number, cursor?: string | null) {
        const queryParams: any = {};
        if (cursor) {
            queryParams.cursor = cursor;
        }

        return this.fitApi.get<{ data: CustomerActivityFeed }>(`/admin/customer/${customerId}/activity`, queryParams);
    }

    public getCustomerQuestionnaires(customerId: number, cursor?: string | null, search?: string) {
        const queryParams: any = {};
        if (cursor) {
            queryParams.cursor = cursor;
        }
        if (search?.trim()) {
            queryParams.search = search.trim();
        }

        return this.fitApi.get<{ data: CustomerQuestionnaireFeed }>(`/admin/customer/${customerId}/questionnaires`, queryParams);
    }

    public getCustomerQuestionnairesBreakdown(customerId: number) {
        return this.fitApi.get<{ data: CustomerQuestionnaireBreakdown }>(`/admin/customer/${customerId}/questionnaires/breakdown`);
    }

    public getWeekActivity(customerId: number, year: number, week: number, timezone?: string) {
        return this.fitApi.get<WeekActivity[]>(
            `/admin/customer/${customerId}/week-activity/${year}/${week}`, 
            timezone ? { timezone: timezone } : {}
        );
    }

    public getCustomerCalendar(customerId: number, period: 'week' | 'month', date?: string) {
        const queryParams: Record<string, string> = { period };
        if (date) {
            queryParams.date = date;
        }

        return this.fitApi.get<CustomerCalendarDay[]>(`/admin/customer/${customerId}/calendar`, queryParams);
    }

    public assignCustomerCalendarActivities(
        customerId: number,
        body: CustomerCalendarActivitiesAssignRequest
    ) {
        return this.fitApi.post<CustomerCalendarDay[]>(
            `/admin/customer/${customerId}/calendar/activities`,
            body
        );
    }

    public moveCustomerCalendarActivity(
        customerId: number,
        eventId: number,
        body: CustomerCalendarActivityMoveRequest
    ) {
        return this.fitApi.put<CustomerCalendarDay[]>(
            `/admin/customer/${customerId}/calendar/activities/${eventId}`,
            body
        );
    }

    public deleteCustomerCalendarActivity(customerId: number, eventId: number) {
        return this.fitApi.delete<null>(
            `/admin/customer/${customerId}/calendar/activities/${eventId}`
        );
    }

    public getUserMeal(id: number) {
        return this.fitApi.get<UserMealDetails>(`/admin/user-meal/${id}`);
    }

    public getHealthData(customerId: number, dataType: string, startDate: string, endDate: string) {
        let params;
        if (dataType) {
            params = {
                start_date: startDate,
                end_date: endDate,
                data_type: dataType
            };
        } else {
            params = {
                start_date: startDate,
                end_date: endDate
            };
        }
        return this.fitApi.get<HealthData[]>(`/admin/customer/${customerId}/health-tracking`, params);
    }

    public getMacronutrientsTracking(customerId: number, startDate: string, endDate: string) {
        return this.fitApi.get<MacronutrientsTrackingDay[]>(
            `/admin/customer/${customerId}/macronutrients-tracking`,
            {
                start_date: startDate,
                end_date: endDate
            }
        );
    }

    public getHealthSummary(customerId: number, startDate: string, endDate: string) {
        const params = {
            start_date: startDate,
            end_date: endDate
        };
        return this.fitApi.get<HealthSummary>(`/admin/customer/${customerId}/health-tracking-summary`, params);
    }

    public getDailyHealthSummary(id: number, date: string) {
        const params = {
            date
        };
        return this.fitApi.get<HealthSummary>(`/admin/customer/${id}/health-tracking-summary/daily`, params);
    }

    public getDailyHealth(id: number, dataType: string, date: string, perHour: boolean = false) {
        const params = {
            date,
            data_type: dataType,
            perHour: perHour.toString()
        }
        return this.fitApi.get<any>(`/admin/customer/${id}/health-tracking/daily`, params);
    }

    public deactivateProgram(activatedProgramId: number) {
        return this.fitApi.post<any>(`/admin/user-program/${activatedProgramId}/deactivate`, {});
    }

    public deactivateNutritionProgram(id: number) {
        return this.fitApi.post<any>(`/admin/nutrition-program/${id}/deactivate`, {});
    }

    private mapAdministratorRequest(admin: Administrator): AdministratorRequest {
        return {
            email: admin.email,
            first_name: admin.first_name,
            gender: admin.gender,
            id: admin.id,
            image_id: admin.image ? admin.image.id : null,
            is_active: admin.is_active,
            last_name: admin.last_name,
            password: admin.password,
        };
    }

    private mapCoachRequest(coach: Coach): CoachRequest {
        return {
            email: coach.email,
            first_name: coach.first_name,
            gender: coach.gender,
            id: coach.id,
            image_id: coach.image ? coach.image.id : null,
            is_active: coach.is_active,
            last_name: coach.last_name,
            password: coach.password,
        };
    }

    private mapMacronutrientsToObject(macronutrients: MacroNutritientItem[]): MacroNutritients {
        const defaults = this.getDefaultMacronutrients();
        for (const item of macronutrients) {
            if (!item || !item.type || !(item.type in defaults)) {
                continue;
            }
            const key = item.type as MacroNutritientType;
            defaults[key] = {
                ...defaults[key],
                ...item,
                requirements: Number(item.requirements ?? 0)
            };
        }
        return defaults;
    }

    private mapMacronutrientsToRequest(macronutrients: MacroNutritients): MacroNutritientsRequest {
        return (Object.keys(macronutrients) as MacroNutritientType[]).reduce((payload, type) => {
            const item = macronutrients[type];
            payload[type] = {
                value: Number(item?.requirements ?? 0),
                unit: item?.unit ?? null
            };
            return payload;
        }, {} as MacroNutritientsRequest);
    }

    private getDefaultMacronutrients(): MacroNutritients {
        return {
            daily_goals: this.createDefaultMacronutrientItem('daily_goals', 'Daily goals', 'kcal'),
            protein: this.createDefaultMacronutrientItem('protein', 'Protein', 'g'),
            carbs: this.createDefaultMacronutrientItem('carbs', 'Carbs', 'g'),
            fats: this.createDefaultMacronutrientItem('fats', 'Fats', 'g'),
            sugar: this.createDefaultMacronutrientItem('sugar', 'Sugar', 'g'),
            fiber: this.createDefaultMacronutrientItem('fiber', 'Fiber', 'g'),
            water: this.createDefaultMacronutrientItem('water', 'Water', 'ounces')
        };
    }

    private createDefaultMacronutrientItem(type: MacroNutritientType, title: string, unit: string): MacroNutritientItem {
        return {
            type,
            title,
            unit,
            requirements: 0,
            intake: 0,
            deficit: 0,
            percentage: 0
        };
    }
}
